import { useEffect, useState } from 'react'
import { useBannerCoupons } from './useBannerCoupons'

/**
 * The announcement strip that scrolls under the navbar at the top of the page.
 *
 * Gold ground, dark type — the one deliberate inversion of the site's palette,
 * which is what makes a 36px band read as an announcement rather than as more
 * chrome.
 *
 * The scroll is pure CSS: the content is rendered TWICE inside a flex track and
 * the animation travels exactly -50%, so the second copy lands where the first
 * began and the loop is seamless. No JS ticks, nothing to keep in sync with the
 * frame rate, and it costs nothing when the tab is backgrounded.
 *
 * It is a WELCOME, not furniture: the strip belongs to the top of the page and
 * slides away for good once the visitor is past the opening. Left pinned it ends
 * up laid over the editorial bands below, where a gold bar is simply noise.
 */

// Shown whenever there is no live offer to announce. Facts about the service,
// all of them true of the real product — never an invented promotion.
const DEFAULT_ITEMS = [
  'FREE SHIPPING ACROSS INDIA',
  'SECURE PAYMENTS',
  'SHIPS IN 7-10 WORKING DAYS',
]

/**
 * Strip height. Exported because the landing page's brand wordmark is positioned
 * by GSAP with an inline `top`, which beats the stylesheet's var() — it needs the
 * number itself, and reading the live custom property there would race with this
 * component's effect on the way back up to the hero.
 */
export const TICKER_HEIGHT = 36
export const TICKER_HEIGHT_MOBILE = 32

const CSS = `
.tpc-ticker { position:fixed; left:0; right:0; top:var(--tpc-nav-h, 62px); z-index:999;
  height:${TICKER_HEIGHT}px; display:flex; align-items:center; overflow:hidden;
  color:#0a0a0f; font-family:'Fira Code',monospace; font-size:11px; letter-spacing:0.15em;
  text-transform:uppercase; font-weight:600; user-select:none;
  /* A hair of tone across the band so it reads as a surface, not a swatch. */
  background:linear-gradient(180deg, #d3b57c 0%, #c9a96e 100%);
  box-shadow:inset 0 -1px 0 rgba(10,10,15,0.16), 0 6px 18px rgba(0,0,0,0.28);
  /* Draws itself open from the centre to both edges once the page has loaded.
     clip-path rather than scaleX: a scale would squash the type and the gold
     gradient along with the box, where a clip just uncovers them at full size.
     The box-shadow is clipped with it, so no glow leaks past the open edge. */
  clip-path:inset(0 0 0 0);
  transition:clip-path 0.8s cubic-bezier(0.62,0.02,0.2,1);
  will-change:clip-path; }
/* Closes back the way it came — symmetry is the point; it never slides off. */
.tpc-ticker.is-gone { clip-path:inset(0 50% 0 50%); pointer-events:none; }

/* Holds the content and fades it a beat AFTER the band has opened, so you read
   the gold band drawing itself first and the announcements settling into it —
   rather than watching text get sliced by the moving edge. */
.tpc-ticker-window { display:flex; align-items:center; width:100%; overflow:hidden;
  opacity:1; transition:opacity 0.45s ease 0.3s;
  -webkit-mask-image:linear-gradient(90deg, transparent 0, #000 44px, #000 calc(100% - 44px), transparent 100%);
  mask-image:linear-gradient(90deg, transparent 0, #000 44px, #000 calc(100% - 44px), transparent 100%); }
/* Going away, the type leaves first and quickly — no delay to wait through. */
.tpc-ticker.is-gone .tpc-ticker-window { opacity:0; transition:opacity 0.18s ease; }
/* Nothing to animate while it's closed. */
.tpc-ticker.is-gone .tpc-ticker-track { animation-play-state:paused; }

/* width:max-content — the track must be as wide as its content, or the flex
   parent would squeeze both copies to the viewport and the -50% travel would
   no longer line up. */
.tpc-ticker-track { display:flex; width:max-content; align-items:center;
  animation:tpc-marquee 34s linear infinite; will-change:transform; }
.tpc-ticker-copy { display:flex; align-items:center; }
.tpc-ticker-item { display:inline-flex; align-items:center; gap:10px; white-space:nowrap; padding:0 26px; }
/* The diamond is a separator, not a bullet — it sits between items at half
   weight so the line reads as one continuous sentence of facts. */
.tpc-ticker-sep { opacity:0.42; font-size:9px; }
.tpc-ticker-code { font-weight:700; padding:1px 7px; border:1px solid rgba(10,10,15,0.38);
  background:rgba(10,10,15,0.06); letter-spacing:0.12em; }
@keyframes tpc-marquee {
  0%   { transform:translateX(0); }
  100% { transform:translateX(-50%); }
}
/* A strip of type sliding forever is exactly what motion sensitivity flags —
   hold it still and let the first copy read as a static line. */
@media (prefers-reduced-motion:reduce) {
  .tpc-ticker-track { animation:none; }
  /* No wipe either — the band and its type appear and leave outright. */
  .tpc-ticker, .tpc-ticker .tpc-ticker-window { transition:none; }
}
@media (max-width:600px) {
  .tpc-ticker { height:${TICKER_HEIGHT_MOBILE}px; font-size:10px; letter-spacing:0.12em; }
  .tpc-ticker-item { padding:0 18px; gap:8px; }
}
`

export function OfferTicker({ hidden = false }: { hidden?: boolean }) {
  const { coupons } = useBannerCoupons()

  // Second reason to retract, and the only one that works on a page whose
  // journey doesn't drive `hidden` (reduced-motion / static mode, where the
  // sections are ordinary scrolling blocks): the visitor has left the opening
  // screen behind. Passive listener — this must never fight the scroll.
  const [scrolledPast, setScrolledPast] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolledPast(window.scrollY > window.innerHeight * 0.5)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const gone = hidden || scrolledPast

  // The strip is fixed, so it adds no height to the document — anything the page
  // positions against the navbar has to reserve that space itself.
  //
  // This is a RESERVATION held for as long as the ticker is mounted, NOT a live
  // height: it deliberately does not drop to 0 while the strip is retracted.
  // Tying it to visibility made the landing wordmark jump by the strip's height
  // the instant it slid in or out. Withdrawn on unmount, so only pages that
  // render a ticker pay for it.
  useEffect(() => {
    const root  = document.documentElement
    const query = window.matchMedia('(max-width:600px)')
    const publish = () => root.style.setProperty(
      '--tpc-ticker-h', `${query.matches ? TICKER_HEIGHT_MOBILE : TICKER_HEIGHT}px`,
    )

    publish()
    // The strip is shorter on phones; a rotation crosses that breakpoint.
    query.addEventListener('change', publish)
    return () => {
      query.removeEventListener('change', publish)
      root.style.removeProperty('--tpc-ticker-h')
    }
  }, [])

  const items = coupons.length > 0
    ? coupons.map((c) => (
        <span className="tpc-ticker-item" key={c.code}>
          <span className="tpc-ticker-sep">✦</span>
          {c.description || `${c.discount_type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`} off`}
          <span className="tpc-ticker-sep">·</span>
          Use code
          <span className="tpc-ticker-code">{c.code}</span>
        </span>
      ))
    : DEFAULT_ITEMS.map((t) => (
        <span className="tpc-ticker-item" key={t}>
          <span className="tpc-ticker-sep">✦</span>
          {t}
        </span>
      ))

  return (
    <div
      className={`tpc-ticker${gone ? ' is-gone' : ''}`}
      role="complementary"
      aria-label="Announcements"
      aria-hidden={gone}
    >
      <style>{CSS}</style>
      <div className="tpc-ticker-window">
        {/* Second copy is presentational only — a screen reader would otherwise
            read every announcement twice. */}
        <div className="tpc-ticker-track">
          <div className="tpc-ticker-copy">{items}</div>
          <div className="tpc-ticker-copy" aria-hidden="true">{items}</div>
        </div>
      </div>
    </div>
  )
}
